/**
 * P2P session orchestrator: signaling WebSocket + WebRTC peer + NaCl crypto.
 *
 * Lifecycle:
 *   1. Generate ephemeral X25519 keypair (closure-scoped).
 *   2. Fetch ICE config from signaling server (STUN/TURN).
 *   3. Open WebSocket to /signal/:roomCode.
 *   4. Create SimplePeer; relay SDP/ICE through the WS.
 *   5. On peer 'connect', close the WS and exchange public keys via DataChannel.
 *   6. All subsequent messages are NaCl-encrypted.
 *
 * @module features/p2p-chat/lib/chat
 */

import { sanitizeText } from '@/shared/lib/sanitize';
import type { Session, StartSessionOptions } from '../types/p2pChat.types';
import {
  decrypt,
  encrypt,
  generateKeyPair,
  publicKeyFromString,
  publicKeyToString,
} from './crypto';
import { getIceConfig } from './turn';

const HANDSHAKE_TIMEOUT_MS = 20_000;
const MAX_MESSAGE_CHARS = 4096;

interface SignalEnvelope {
  readonly type: 'signal';
  readonly payload: unknown;
}

interface HelloFrame {
  readonly type: 'hello';
  readonly pk: string;
}

interface MsgFrame {
  readonly type: 'msg';
  readonly data: string;
}

type DataFrame = HelloFrame | MsgFrame;

export async function startSession(
  opts: StartSessionOptions
): Promise<Session> {
  const {
    roomCode,
    initiator,
    signalHttpUrl,
    signalWsUrl,
    callbacks,
    relayOnly,
  } = opts;
  const { onMessage, onStatusChange, onError } = callbacks;

  // Closure-captured. secretKey never escapes this function.
  const myKeys = generateKeyPair();
  let theirPublicKey: Uint8Array | null = null;
  let connectedFired = false;
  let closed = false;

  onStatusChange('connecting');

  const iceConfig = await getIceConfig(signalHttpUrl);
  if (relayOnly) {
    iceConfig.iceTransportPolicy = 'relay';
  }

  // Dynamic import keeps simple-peer + Buffer dep tree out of the initial bundle.
  // Use the pre-bundled UMD build which inlines Node polyfills (events, util,
  // readable-stream) that Vite would otherwise externalize for the browser.
  const { default: SimplePeer } = await import('simple-peer/simplepeer.min.js');

  const ws = new WebSocket(`${signalWsUrl}/signal/${roomCode}`);

  // Buffer outbound signal frames produced before the WS reaches OPEN.
  // SimplePeer (especially as initiator) emits its first 'signal' nearly
  // synchronously after construction, well before the WS handshake completes.
  // Without this queue those frames are silently dropped and the remote peer
  // never receives the offer, causing a 20s handshake timeout.
  const outboundQueue: string[] = [];

  const sendSignal = (payload: unknown): void => {
    const frame = JSON.stringify({
      type: 'signal',
      payload,
    } satisfies SignalEnvelope);
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(frame);
    } else if (ws.readyState === WebSocket.CONNECTING) {
      outboundQueue.push(frame);
    }
    // CLOSING/CLOSED: drop — peer is being torn down.
  };

  const peer = new SimplePeer({
    initiator,
    trickle: true,
    config: iceConfig,
  });

  ws.onopen = (): void => {
    onStatusChange('awaiting-peer');
    while (outboundQueue.length > 0) {
      const frame = outboundQueue.shift();
      if (frame !== undefined) ws.send(frame);
    }
  };

  ws.onmessage = (e: MessageEvent<string>): void => {
    if (typeof e.data !== 'string') return;
    let envelope: SignalEnvelope;
    try {
      envelope = JSON.parse(e.data) as SignalEnvelope;
    } catch {
      return;
    }
    if (envelope.type !== 'signal' || envelope.payload === undefined) return;
    try {
      peer.signal(envelope.payload as never);
    } catch (err) {
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  ws.onclose = (): void => {
    if (!connectedFired && !closed) {
      onStatusChange('disconnected');
    }
  };

  ws.onerror = (): void => {
    onError(new Error('Signaling connection failed'));
  };

  peer.on('signal', (data) => {
    sendSignal(data);
  });

  peer.on('connect', () => {
    connectedFired = true;
    onStatusChange('handshaking');
    try {
      ws.close();
    } catch {
      /* swallow */
    }
    const hello: HelloFrame = {
      type: 'hello',
      pk: publicKeyToString(myKeys.publicKey),
    };
    peer.send(JSON.stringify(hello));
  });

  peer.on('data', (raw: Uint8Array | string) => {
    let frame: DataFrame;
    try {
      const text =
        typeof raw === 'string' ? raw : new TextDecoder().decode(raw);
      frame = JSON.parse(text) as DataFrame;
    } catch {
      return;
    }
    if (frame.type === 'hello') {
      try {
        theirPublicKey = publicKeyFromString(frame.pk);
        onStatusChange('connected');
      } catch {
        /* malformed key — silently ignore */
      }
      return;
    }
    if (frame.type === 'msg' && theirPublicKey) {
      const plain = decrypt(frame.data, theirPublicKey, myKeys.secretKey);
      if (plain !== null) onMessage(plain);
    }
  });

  peer.on('close', () => {
    closed = true;
    onStatusChange('disconnected');
  });

  peer.on('error', (err) => {
    onError(err instanceof Error ? err : new Error(String(err)));
    onStatusChange('failed');
  });

  // Handshake timeout — if the public-key exchange has not completed, abort.
  const timeoutHandle = setTimeout(() => {
    if (theirPublicKey === null && !closed) {
      onError(new Error('Connection timeout'));
      try {
        peer.destroy();
      } catch {
        /* swallow */
      }
    }
  }, HANDSHAKE_TIMEOUT_MS);

  return {
    send(text: string): boolean {
      if (!theirPublicKey || peer.destroyed) return false;
      const sanitized = sanitizeText(text, { maxLength: MAX_MESSAGE_CHARS });
      if (!sanitized) return false;
      try {
        const cipher = encrypt(sanitized, theirPublicKey, myKeys.secretKey);
        const frame: MsgFrame = { type: 'msg', data: cipher };
        peer.send(JSON.stringify(frame));
        return true;
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
        return false;
      }
    },
    close(): void {
      closed = true;
      clearTimeout(timeoutHandle);
      try {
        peer.destroy();
      } catch {
        /* swallow */
      }
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        try {
          ws.close();
        } catch {
          /* swallow */
        }
      }
    },
  };
}
